import React, { useState } from 'react';
import {
  CheckCircle2,
  Circle,
  Edit3,
  Save,
  X,
  Lock,
  Unlock,
  Paperclip,
  Zap
} from 'lucide-react';
import { RoadmapStepValidation, RoadmapStep } from '../../types/roadmap';
import { isStepValidated, getValidationType } from '../../utils/roadmapAutoValidation';

interface RoadmapStepItemProps {
  step: RoadmapStep;
  validation: RoadmapStepValidation | null;
  onUpdateValidation: (data: {
    is_manually_validated?: boolean;
    note?: string;
  }) => Promise<void>;
  onForceValidation: (force: boolean) => Promise<void>;
  isEditing: boolean;
  onToggleEdit: () => void;
}

export const RoadmapStepItem: React.FC<RoadmapStepItemProps> = ({
  step,
  validation,
  onUpdateValidation,
  onForceValidation,
  isEditing,
  onToggleEdit
}) => {
  const [note, setNote] = useState(validation?.note || '');
  const [isSaving, setIsSaving] = useState(false);

  const isValidated = isStepValidated(validation);
  const validationType = getValidationType(validation);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdateValidation({ note });
      onToggleEdit();
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleManualValidation = async () => {
    setIsSaving(true);
    try {
      const newValue = !validation?.is_manually_validated;
      await onUpdateValidation({
        is_manually_validated: newValue,
        note: validation?.note || undefined
      });
    } catch (error) {
      console.error('Error toggling validation:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleForceValidation = async (force: boolean) => {
    setIsSaving(true);
    try {
      await onForceValidation(force);
    } catch (error) {
      console.error('Error forcing validation:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`border rounded-lg p-4 transition-all ${
        isValidated
          ? 'bg-green-50 border-green-200'
          : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={handleToggleManualValidation}
          disabled={isSaving}
          className="mt-1 flex-shrink-0 disabled:opacity-50"
        >
          {isValidated ? (
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          ) : (
            <Circle className="w-6 h-6 text-gray-300 hover:text-gray-400" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">{step.label}</h4>
              <p className="text-sm text-gray-600 mt-1">{step.description}</p>
            </div>

            <div className="flex items-center gap-2">
              {validationType === 'auto' && (
                <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                  <Zap className="w-3 h-3" />
                  Auto
                </span>
              )}
              {validationType === 'manual' && (
                <span className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                  Manuel
                </span>
              )}
              {validationType === 'both' && (
                <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                  <Zap className="w-3 h-3" />
                  Auto + Manuel
                </span>
              )}
            </div>
          </div>

          {validation?.is_auto_validated && !validation?.is_manually_validated && (
            <div className="mb-3 flex items-center gap-2">
              <button
                onClick={() => handleForceValidation(true)}
                disabled={isSaving}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
              >
                <Lock className="w-3 h-3 inline mr-1" />
                Forcer la validation
              </button>
              <button
                onClick={() => handleForceValidation(false)}
                disabled={isSaving}
                className="text-xs text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
              >
                <Unlock className="w-3 h-3 inline mr-1" />
                Annuler la validation auto
              </button>
            </div>
          )}

          {isEditing ? (
            <div className="mt-3 space-y-2">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Ajouter une note..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-3 h-3" />
                  Enregistrer
                </button>
                <button
                  onClick={() => {
                    setNote(validation?.note || '');
                    onToggleEdit();
                  }}
                  disabled={isSaving}
                  className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 disabled:opacity-50"
                >
                  <X className="w-3 h-3" />
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              {validation?.note ? (
                <div className="bg-gray-50 rounded p-3 mb-2">
                  <p className="text-sm text-gray-700">{validation.note}</p>
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                <button
                  onClick={onToggleEdit}
                  className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-700"
                >
                  <Edit3 className="w-3 h-3" />
                  {validation?.note ? 'Modifier la note' : 'Ajouter une note'}
                </button>
              </div>
            </div>
          )}

          {validation?.validation_date && (
            <p className="text-xs text-gray-500 mt-2">
              Validée le {new Date(validation.validation_date).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
